'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Panel } from '@/components/ui/panel';
import { Link } from '@/i18n/navigation';
import { useUserNetwork } from '@/lib/network/client';
import { isMainnetEnabled } from '@/lib/network/mainnet-enabled';
import { useSwitchNetwork } from '@/lib/network/switcher';
import { useMainnetConstructionModalStore } from '@/stores/mainnet-construction-modal';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type DemoNotFoundKind = 'market' | 'proposal';
interface DemoNotFoundCardProps {
  /** Drives copy + browse-link target. */
  kind: DemoNotFoundKind;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Graceful fallback rendered when a demo-mode (devnet) URL points at a
 * market/proposal that no longer exists. Devnet state is reset
 * periodically, so bookmarked URLs go stale; we explain that and offer
 * to switch the user to mainnet.
 */
export function DemoNotFoundCard(t0) {
  const $ = _c(30);
  const {
    kind
  } = t0;
  const t = useTranslations("demoNotFound");
  const switchNetwork = useSwitchNetwork();
  const {
    toggleEnabled: envToggleEnabled
  } = useUserNetwork();
  const openConstructionModal = useMainnetConstructionModalStore(_temp);
  const titleKey = kind === "market" ? "marketTitle" : "proposalTitle";
  const descriptionKey = kind === "market" ? "marketDescription" : "proposalDescription";
  const browseKey = kind === "market" ? "browseMarkets" : "browseProposals";
  const browseHref = kind === "market" ? "/markets" : "/proposals";
  let t1;
  if ($[0] !== openConstructionModal || $[1] !== switchNetwork) {
    t1 = function handleSwitchToMainnet() {
      if (!isMainnetEnabled()) {
        openConstructionModal(true);
        return;
      }
      switchNetwork("mainnet");
    };
    $[0] = openConstructionModal;
    $[1] = switchNetwork;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleSwitchToMainnet = t1;
  let t2;
  if ($[3] !== t || $[4] !== titleKey) {
    t2 = t(titleKey);
    $[3] = t;
    $[4] = titleKey;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <h1 className="text-heading-md text-foreground-primary">{t2}</h1>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== descriptionKey || $[9] !== t) {
    t4 = t(descriptionKey);
    $[8] = descriptionKey;
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <p className="max-w-xl text-body text-foreground-secondary">{t4}</p>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== browseKey || $[14] !== t) {
    t6 = t(browseKey);
    $[13] = browseKey;
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== browseHref || $[17] !== t6) {
    t7 = <AnimatedButton asChild={true} variant="secondary"><Link href={browseHref}>{t6}</Link></AnimatedButton>;
    $[16] = browseHref;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== envToggleEnabled || $[20] !== handleSwitchToMainnet || $[21] !== t) {
    t8 = envToggleEnabled ? <AnimatedButton variant="primary" onClick={handleSwitchToMainnet}>{t("switchToMainnet")}</AnimatedButton> : null;
    $[19] = envToggleEnabled;
    $[20] = handleSwitchToMainnet;
    $[21] = t;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = <div className="mt-2 flex flex-col items-center justify-center gap-2 sm:flex-row">{t7}{t8}</div>;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t3 || $[27] !== t5 || $[28] !== t9) {
    t10 = <main className="mx-auto w-full max-w-3xl px-4 py-10"><Panel className="items-center gap-4 px-6 py-10 text-center">{t3}{t5}{t9}</Panel></main>;
    $[26] = t3;
    $[27] = t5;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}
function _temp(s) {
  return s.setOpen;
}